import React, { useEffect, useState } from 'react';
import { DashboardModal } from 'containers/Dashboard/components/DashboardModal';
import { Alert, Button, Table } from 'reactstrap';
import Dropzone from 'react-dropzone';
import { useSelector } from 'react-redux';
import Loader from 'components/Loader';
import __ from 'utils/i18n';
import { notification } from 'utils/notifications';
import { backendUrl, tenantId } from 'config/envConfig';
import { PlanInviteCsvValidator } from 'utils/planInviteCsvValidator';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { sendPlanInvites } from '../../_shared/resources';
import { BackendRpErrorResponse } from '../../../_shared/BackendRpErrorResponse';

export const InviteByEmailModal = ({
  showModal = false,
  hideModal,
  plan,
}) => {
  const { token } = useSelector((state) => state.api);
  const [file, setFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [currentStep, setCurrentStep] = useState('upload'); // upload | validated | result | error
  const [simpleError, setSimpleError] = useState(null);

  const validator = new PlanInviteCsvValidator();

  useEffect(() => {
    if (!showModal) {
      setFile(null);
      setValidationResult(null);
      setSendResult(null);
      setCurrentStep('upload');
      setIsValidating(false);
      setIsSending(false);
      setSimpleError(null);
    }
  }, [showModal]);

  const handleDownloadTemplate = async () => {
    if (!token || !plan?.planId) {
      notification.error(__('dashboard.sections.recurringPayments.plans.invite.errors.auth'));
      return;
    }

    setIsDownloadingTemplate(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/v1/recurring-payments/plans/${plan.planId}/invite-template`,
        {
          method: 'GET',
          headers: {
            'x-client-id': tenantId,
            'x-Authentication-Token': token,
            'x-request-download': 'stream',
          },
        },
      );

      if (!response.ok) {
        throw new Error('download_failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      const sanitizedPlanName = (plan.name || 'Plan').replace(/[^a-zA-Z0-9\s]/g, '').trim();
      let filename = `invitar_${plan.planId}_${sanitizedPlanName}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      notification.error(__('dashboard.sections.recurringPayments.plans.invite.errors.downloadTemplate'));
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleDrop = async (acceptedFiles) => {
    setSimpleError(null);
    setSendResult(null);
    setValidationResult(null);

    const selectedFile = acceptedFiles?.[0];
    if (!selectedFile) {
      setSimpleError(__('dashboard.sections.recurringPayments.plans.invite.errors.requiredFile'));
      setCurrentStep('error');
      return;
    }

    setFile(selectedFile);
    setIsValidating(true);
    setCurrentStep('upload');

    const result = await validator.validateFile(selectedFile);
    setIsValidating(false);

    if (!result.ok) {
      setSimpleError(result.error);
      setValidationResult(result);
      setCurrentStep('error');
      return;
    }

    setValidationResult(result);
    setCurrentStep('validated');
  };

  const handleSend = async () => {
    if (!validationResult?.validContacts?.length || !plan?.planId || !token) return;

    setIsSending(true);
    try {
      const response = await fetchResourceRP(
        sendPlanInvites(plan.planId, validationResult.validContacts, token),
      );

      const payload = response?.data?.data || response?.data || response || {};
      setSendResult({
        sent: payload.sent ?? 0,
        failed: payload.failed ?? 0,
        failures: payload.failures || [],
      });
      setCurrentStep('result');
    } catch (error) {
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
      setSimpleError(BackendRpErrorResponse.firstErrorMessage(error));
      setCurrentStep('error');
    } finally {
      setIsSending(false);
    }
  };

  const renderBody = () => {
    if (currentStep === 'result' && sendResult) {
      return (
        <div>
          <Alert color={sendResult.failed > 0 ? 'warning' : 'success'}>
            {__('dashboard.sections.recurringPayments.plans.invite.resultSummary', {
              sent: sendResult.sent,
              failed: sendResult.failed,
            })}
          </Alert>
          {sendResult.failures?.length > 0 && (
            <div className="table-responsive" style={{ maxHeight: 240, overflowY: 'auto' }}>
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>{__('dashboard.sections.recurringPayments.plans.invite.email')}</th>
                    <th>{__('dashboard.sections.recurringPayments.plans.invite.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sendResult.failures.map((failure) => (
                    <tr key={`${failure.email}-${failure.reason}`}>
                      <td>{failure.email || '-'}</td>
                      <td>{failure.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <p className="text-muted mb-3">
          {__('dashboard.sections.recurringPayments.plans.invite.description')}
        </p>

        <div className="mb-3">
          <Button
            color="link"
            className="p-0"
            onClick={handleDownloadTemplate}
            disabled={isDownloadingTemplate || isSending}
          >
            <i className="mdi mdi-download mr-1" />
            {isDownloadingTemplate
              ? '...'
              : __('dashboard.sections.recurringPayments.plans.invite.downloadTemplate')}
          </Button>
        </div>

        <Dropzone onDrop={handleDrop} multiple={false} accept=".csv" disabled={isValidating || isSending}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className="border rounded p-4 text-center mb-3"
              style={{
                cursor: 'pointer',
                backgroundColor: isDragActive ? '#f8f9fa' : '#fff',
              }}
            >
              <input {...getInputProps()} />
              {isValidating ? (
                <Loader />
              ) : (
                <>
                  <i className="mdi mdi-cloud-upload mdi-36px text-muted d-block mb-2" />
                  <div>
                    {file
                      ? file.name
                      : __('dashboard.sections.recurringPayments.plans.invite.dropzone')}
                  </div>
                </>
              )}
            </div>
          )}
        </Dropzone>

        {simpleError && (
          <Alert color="danger">{simpleError}</Alert>
        )}

        {validationResult?.ok && (
          <Alert color="info">
            {__('dashboard.sections.recurringPayments.plans.invite.previewSummary', {
              valid: validationResult.validContacts.length,
              invalid: validationResult.invalidRows?.length || 0,
            })}
          </Alert>
        )}

        {validationResult?.invalidRows?.length > 0 && (
          <div className="table-responsive mb-3" style={{ maxHeight: 200, overflowY: 'auto' }}>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>{__('dashboard.sections.recurringPayments.plans.invite.row')}</th>
                  <th>{__('dashboard.sections.recurringPayments.plans.invite.email')}</th>
                  <th>{__('dashboard.sections.recurringPayments.plans.invite.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {validationResult.invalidRows.slice(0, 50).map((row) => (
                  <tr key={`invalid-${row.row}-${row.email}`}>
                    <td>{row.row}</td>
                    <td>{row.email || '-'}</td>
                    <td>{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardModal
      name="inviteByEmailModal"
      headerText={__('dashboard.sections.recurringPayments.plans.invite.title')}
      showModal={showModal}
      hideModal={hideModal}
      showFooter={false}
      hideSeparator={true}
      size="lg"
    >
      {renderBody()}

      {currentStep !== 'result' && (
        <div className="d-flex justify-content-end mt-3">
          <Button color="secondary" className="mr-2" onClick={hideModal} disabled={isSending}>
            {__('actions.cancel')}
          </Button>
          <Button
            color="primary"
            onClick={handleSend}
            disabled={
              isSending ||
              isValidating ||
              currentStep !== 'validated' ||
              !validationResult?.validContacts?.length
            }
          >
            {isSending
              ? `${__('dashboard.sections.recurringPayments.plans.invite.send')}...`
              : __('dashboard.sections.recurringPayments.plans.invite.send')}
          </Button>
        </div>
      )}
    </DashboardModal>
  );
};
